import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, removeToken } from "../App";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Trash2,
  Users,
  ChevronLeft,
  ChevronRight,
  BadgeCheck,
  Lock,
  Globe,
  Heart,
  Grid,
  Download,
  Loader2,
  ExternalLink,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import BotCard from "../components/BotCard";

export default function ProfilesPage() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    week: 0,
    month: 0,
  });
  const [exportStatus, setExportStatus] = useState({
    available: false,
    filename: "",
    minutes_remaining: 0,
  });
  const [generating, setGenerating] = useState(false);
  const perPage = 10;
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/profiles/stats`);
      setStats({
        total: response.data.total || 0,
        today: response.data.today || 0,
        week:  response.data.week  || 0,
        month: response.data.month || 0,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        removeToken();
        navigate("/login");
      }
    }
  }, [navigate]);

  const fetchProfiles = useCallback(async () => {
    if (initialLoad) {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${API}/profiles?page=${page}&per_page=${perPage}`);
      const profilesList = response.data.data || [];
      
      // Para cada perfil, buscar os dados completos
      const fullProfiles = await Promise.all(
        profilesList.map(async (p) => {
          try {
            const detail = await axios.get(`${API}/profiles/${p.id}`);
            return detail.data;
          } catch {
            return p;
          }
        })
      );
      
      setProfiles(fullProfiles);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      if (error.response?.status === 401) {
        removeToken();
        navigate("/login");
      } else {
        if (initialLoad) {
          toast.error("Falha ao carregar perfis");
        }
      }
    } finally {
      if (initialLoad) {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  }, [navigate, page, initialLoad]);

  useEffect(() => {
    fetchProfiles();
    fetchStats();
    checkExportStatus();
    
    // Atualizar a cada 5 segundos
    const interval = setInterval(() => {
      fetchProfiles();
      fetchStats();
      checkExportStatus();
    }, 5000);
    
    // Limpar intervalo quando componente desmontar
    return () => clearInterval(interval);
  }, [fetchProfiles, fetchStats]);

  const checkExportStatus = async () => {
    try {
      const response = await axios.get(`${API}/profiles/export/status`);
      setExportStatus(response.data);
    } catch (error) {
      console.error("Erro ao verificar status de export:", error);
    }
  };

  const handleGenerateExport = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/profiles/export/generate`);
      toast.success("Exportação gerada com sucesso!");
      checkExportStatus();
    } catch (error) {
      toast.error("Erro ao gerar exportação");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadExport = async () => {
    try {
      const response = await axios.get(`${API}/profiles/export/download`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', exportStatus.filename || 'profiles_export.json');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download iniciado!");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Arquivo expirado. Gere uma nova exportação.");
        checkExportStatus();
      } else {
        toast.error("Erro ao fazer download");
      }
    }
  };

  const handleDeleteProfile = async () => {
    if (!selectedProfile) return;

    try {
      await axios.delete(`${API}/profiles/${selectedProfile.id}`);
      toast.success("Perfil excluído com sucesso");
      setDeleteDialogOpen(false);
      setSelectedProfile(null);
      fetchProfiles();
    } catch (error) {
      toast.error(error.response?.data?.error || "Falha ao excluir perfil");
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num?.toString() || "0";
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const ProfileCardSkeleton = () => (
    <div className="bg-[#0A0A0A] border border-[#222222] p-4">
      <div className="flex items-center gap-3 mb-3">
        <Skeleton className="w-16 h-16 rounded-full bg-[#222222]" />
        <div className="flex-1">
          <Skeleton className="h-5 w-32 bg-[#222222] mb-2" />
          <Skeleton className="h-4 w-24 bg-[#222222]" />
        </div>
      </div>
      <div className="flex gap-4 mb-3">
        <Skeleton className="h-10 w-16 bg-[#222222]" />
        <Skeleton className="h-10 w-16 bg-[#222222]" />
        <Skeleton className="h-10 w-16 bg-[#222222]" />
      </div>
      <Skeleton className="h-12 w-full bg-[#222222]" />
    </div>
  );

  return (
    <div data-testid="profiles-page" className="min-h-screen bg-[#000000] flex">
      <Sidebar activeMenu="profiles" />

      <main className="flex-1 md:ml-56 pb-20 md:pb-0">
        <header className="h-14 md:h-16 border-b border-[#222222] flex items-center justify-between px-4 md:px-8 bg-[#0A0A0A]">
          <h1 className="text-lg md:text-xl font-bold text-[#EDEDED] font-heading">
            Perfis Instagram
          </h1>
          
          {/* Export Button */}
          <div className="flex items-center gap-2">
            {exportStatus.available ? (
              <div className="flex items-center gap-2">
                <span className="hidden md:inline text-xs text-[#888888]">
                  Expira em {exportStatus.minutes_remaining}min
                </span>
                <Button
                  onClick={handleDownloadExport}
                  className="bg-[#00F0FF] text-black hover:bg-[#00D0E0] rounded-sm font-body text-xs md:text-sm h-8 md:h-9"
                  size="sm"
                >
                  <Download className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Baixar JSON</span>
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleGenerateExport}
                disabled={generating}
                className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-sm font-body text-xs md:text-sm h-8 md:h-9"
                size="sm"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Download className="w-4 h-4 md:mr-2" />
                    <span className="hidden md:inline">Gerar Export</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </header>

        <div className="p-4 md:p-8">
          {/* Bot Card */}
          <BotCard />

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
            {loading && initialLoad ? (
              <>
                <div className="bg-[#0A0A0A] border border-[#222222] p-4 md:p-6">
                  <Skeleton className="h-8 md:h-10 w-12 md:w-16 bg-[#222222] mb-2" />
                  <Skeleton className="h-3 md:h-4 w-16 md:w-20 bg-[#222222]" />
                </div>
                <div className="bg-[#0A0A0A] border border-[#222222] p-4 md:p-6">
                  <Skeleton className="h-8 md:h-10 w-12 md:w-16 bg-[#222222] mb-2" />
                  <Skeleton className="h-3 md:h-4 w-16 md:w-20 bg-[#222222]" />
                </div>
                <div className="bg-[#0A0A0A] border border-[#222222] p-4 md:p-6">
                  <Skeleton className="h-8 md:h-10 w-12 md:w-16 bg-[#222222] mb-2" />
                  <Skeleton className="h-3 md:h-4 w-16 md:w-20 bg-[#222222]" />
                </div>
                <div className="bg-[#0A0A0A] border border-[#222222] p-4 md:p-6">
                  <Skeleton className="h-8 md:h-10 w-12 md:w-16 bg-[#222222] mb-2" />
                  <Skeleton className="h-3 md:h-4 w-16 md:w-20 bg-[#222222]" />
                </div>
              </>
            ) : (
              <>
                <div className="bg-[#0A0A0A] border border-[#222222] p-4 md:p-6">
                  <p className="text-2xl md:text-3xl font-bold font-mono text-[#00FF94]">{stats.total}</p>
                  <p className="text-xs md:text-sm text-[#888888] font-body mt-1">Total</p>
                </div>
                <div className="bg-[#0A0A0A] border border-[#222222] p-4 md:p-6">
                  <p className="text-2xl md:text-3xl font-bold font-mono text-[#00F0FF]">{stats.today}</p>
                  <p className="text-xs md:text-sm text-[#888888] font-body mt-1">Hoje</p>
                </div>
                <div className="bg-[#0A0A0A] border border-[#222222] p-4 md:p-6">
                  <p className="text-2xl md:text-3xl font-bold font-mono text-[#FF3B30]">{stats.week}</p>
                  <p className="text-xs md:text-sm text-[#888888] font-body mt-1">7 Dias</p>
                </div>
                <div className="bg-[#0A0A0A] border border-[#222222] p-4 md:p-6">
                  <p className="text-2xl md:text-3xl font-bold font-mono text-[#FFD60A]">{stats.month}</p>
                  <p className="text-xs md:text-sm text-[#888888] font-body mt-1">30 Dias</p>
                </div>
              </>
            )}
          </div>

          {/* Profiles List */}
          {loading && initialLoad ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {[...Array(2)].map((_, i) => (
                <ProfileCardSkeleton key={i} />
              ))}
            </div>
          ) : profiles.length === 0 ? (
            <div className="bg-[#0A0A0A] border border-[#222222] p-8 md:p-12 text-center">
              <Users className="w-12 h-12 text-[#333333] mx-auto mb-4" />
              <p className="text-[#888888] font-body">Nenhum perfil salvo</p>
              <p className="text-[#555555] text-sm mt-2 font-body">
                Use a API /rotabotv1 para buscar e salvar perfis
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                {profiles.map((profile) => (
                  <div
                    key={profile.id}
                    data-testid={`profile-card-${profile.id}`}
                    className="bg-[#0A0A0A] border border-[#222222] overflow-hidden"
                  >
                    {/* Profile Header */}
                    <div className="p-4 border-b border-[#222222]">
                      <div className="flex items-start gap-4">
                        {/* Avatar */}
                        <img
                          src={profile.profile_pic_url_hd || profile.profile_pic_url || "https://via.placeholder.com/64"}
                          alt={profile.username}
                          className="w-16 h-16 rounded-full object-cover border-2 border-[#222222] flex-shrink-0"
                          onError={(e) => { e.target.src = "https://via.placeholder.com/64"; }}
                        />

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h2 className="text-lg font-bold text-[#EDEDED] font-heading truncate">
                              {profile.full_name || profile.username}
                            </h2>
                            {profile.is_verified && (
                              <BadgeCheck className="w-4 h-4 text-[#00F0FF] flex-shrink-0" />
                            )}
                            {profile.is_private && (
                              <Lock className="w-3 h-3 text-[#888888] flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-[#888888] font-mono text-sm mb-3">
                            <a
                              href={`https://www.instagram.com/${profile.username}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 hover:text-[#00FF94] transition-colors"
                            >
                              @{profile.username}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </p>

                          {/* Stats Row */}
                          <div className="flex gap-4">
                            <div className="flex items-center gap-1.5">
                              <Users className="w-3.5 h-3.5 text-[#00FF94]" />
                              <span className="text-sm font-bold text-[#EDEDED] font-mono">
                                {formatNumber(profile.followers)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Heart className="w-3.5 h-3.5 text-[#FF3B30]" />
                              <span className="text-sm font-bold text-[#EDEDED] font-mono">
                                {formatNumber(profile.following)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Grid className="w-3.5 h-3.5 text-[#00F0FF]" />
                              <span className="text-sm font-bold text-[#EDEDED] font-mono">
                                {formatNumber(profile.posts_count)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Delete Button */}
                        <Button
                          data-testid={`delete-profile-${profile.id}`}
                          onClick={() => {
                            setSelectedProfile(profile);
                            setDeleteDialogOpen(true);
                          }}
                          variant="ghost"
                          size="sm"
                          className="text-[#FF3B30] hover:text-white hover:bg-[#FF3B30] rounded-sm flex-shrink-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>

                    {/* Biography */}
                    {profile.biography && (
                      <div className="px-4 py-3 border-b border-[#222222]">
                        <p className="text-[#AAAAAA] whitespace-pre-wrap font-body text-xs leading-relaxed">
                          {profile.biography}
                        </p>
                      </div>
                    )}

                    {/* Highlights */}
                    {profile.highlights && profile.highlights.length > 0 && (
                      <div className="px-4 py-3 border-b border-[#222222]">
                        <h3 className="text-xs font-bold text-[#888888] mb-3 font-heading uppercase tracking-wider">
                          Destaques ({profile.highlights.length})
                        </h3>
                        <div className="flex gap-3 overflow-x-auto pb-2">
                          {profile.highlights.map((highlight, index) => (
                            <div key={highlight.id || index} className="flex-shrink-0 text-center">
                              <div className="w-14 h-14 rounded-full border-2 border-[#00FF94] p-0.5 mb-1.5">
                                <img
                                  src={highlight.thumbnail_url || "https://via.placeholder.com/56"}
                                  alt={highlight.title}
                                  className="w-full h-full rounded-full object-cover"
                                  onError={(e) => { e.target.src = "https://via.placeholder.com/56"; }}
                                />
                              </div>
                              <p className="text-[10px] text-[#888888] truncate w-14 font-body">
                                {highlight.title || "Destaque"}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="px-4 py-2 flex justify-between items-center bg-[#050505]">
                      <span className="text-[10px] text-[#555555] font-mono">
                        ID: {profile.user_id}
                      </span>
                      <span className="text-[10px] text-[#555555] font-mono">
                        {formatDate(profile.updated_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col md:flex-row items-center justify-between mt-4 md:mt-6 p-4 bg-[#0A0A0A] border border-[#222222] gap-3 md:gap-0">
                  <p className="text-xs md:text-sm text-[#888888] font-body">
                    Mostrando {((page - 1) * perPage) + 1} - {Math.min(page * perPage, total)} de {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      variant="ghost"
                      size="sm"
                      className="text-[#888888] hover:text-[#EDEDED] hover:bg-[#111111] disabled:opacity-30 rounded-sm"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-[#EDEDED] font-mono px-2">
                      {page} / {totalPages}
                    </span>
                    <Button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      variant="ghost"
                      size="sm"
                      className="text-[#888888] hover:text-[#EDEDED] hover:bg-[#111111] disabled:opacity-30 rounded-sm"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0A0A0A] border-[#222222] w-[calc(100vw-2rem)] max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#EDEDED] font-heading text-base md:text-lg">Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-[#888888] font-body text-sm">
              Tem certeza que deseja excluir o perfil de{" "}
              <span className="text-[#EDEDED] font-mono">@{selectedProfile?.username}</span>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="bg-[#111111] border-[#333333] text-[#EDEDED] hover:bg-[#222222] rounded-sm font-body w-full sm:w-auto">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProfile}
              className="bg-[#FF3B30] text-white hover:bg-[#D63025] rounded-sm font-body w-full sm:w-auto"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
